import {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type ComponentType,
} from "react";
import { ChevronLeft, Search } from "lucide-react";
import { MDXProvider } from "@mdx-js/react";
import { Input } from "@heycircle/ui/input";
import { Button } from "@heycircle/ui/button";
import { useTranslation } from "@/services/i18n/client";
import { getArticle, getCategories, type HelpArticle, type HelpLocale } from "../articles";
import { useHelpSearch } from "../use-help-search";

interface HelpBrowserProps {
  initialSlug?: string | null;
  onSlugChange?: (slug: string | null) => void;
}

function resolveLocale(language: string | undefined): HelpLocale {
  return language && language.toLowerCase().startsWith("de") ? "de" : "en";
}

function ArticleView({
  article,
  onNavigate,
}: {
  article: HelpArticle;
  onNavigate: (slug: string) => void;
}) {
  const Component = useMemo<ComponentType<unknown>>(() => lazy(() => article.load()), [article]);
  // Intercept internal help links (/app/help?article=<slug>) so they switch
  // articles in place — keeping the help sheet open and preserving any
  // in-progress form input on the underlying page — instead of triggering a
  // full document navigation. External/other hrefs stay plain anchors.
  const mdxComponents = useMemo(
    () => ({
      a: ({ href, children, ...rest }: ComponentPropsWithoutRef<"a">) => {
        const articleSlug = href?.startsWith("/app/help")
          ? new URL(href, window.location.origin).searchParams.get("article")
          : null;
        if (articleSlug) {
          return (
            <a
              href={href}
              {...rest}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(articleSlug);
              }}
            >
              {children}
            </a>
          );
        }
        return (
          <a href={href} rel="noreferrer" {...rest}>
            {children}
          </a>
        );
      },
    }),
    [onNavigate]
  );
  return (
    <article className="prose dark:prose-invert max-w-none">
      <Suspense fallback={<div className="text-sm text-muted-foreground">Loading…</div>}>
        <MDXProvider components={mdxComponents}>
          <Component />
        </MDXProvider>
      </Suspense>
    </article>
  );
}

export function HelpBrowser({ initialSlug, onSlugChange }: HelpBrowserProps) {
  const { t, i18n } = useTranslation("help");
  const locale = resolveLocale(i18n.resolvedLanguage ?? i18n.language);
  const [slug, setSlug] = useState<string | null>(initialSlug ?? null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setSlug(initialSlug ?? null);
  }, [initialSlug]);

  const selectSlug = (next: string | null) => {
    setSlug(next);
    onSlugChange?.(next);
  };

  const article = slug ? getArticle(locale, slug) : undefined;
  const results = useHelpSearch(locale, query);
  const grouped = useMemo(() => {
    const cats = getCategories(locale);
    const allowed = new Set(results.map((r) => r.article.slug));
    return {
      concepts: cats.concepts.filter((a) => allowed.has(a.slug)),
      flows: cats.flows.filter((a) => allowed.has(a.slug)),
      glossary: cats.glossary.filter((a) => allowed.has(a.slug)),
    };
  }, [locale, results]);

  if (article) {
    return (
      <div className="flex h-full flex-col gap-4 overflow-hidden">
        <Button variant="ghost" size="sm" className="self-start" onClick={() => selectSlug(null)}>
          <ChevronLeft className="h-4 w-4" />
          {t("backToIndex")}
        </Button>
        <div className="flex-1 overflow-y-auto pr-2">
          <ArticleView article={article} onNavigate={selectSlug} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label={t("searchPlaceholder")}
          placeholder={t("searchPlaceholder")}
          className="pl-9"
          value={query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      <div className="flex-1 space-y-6 overflow-y-auto pr-2">
        {(["concepts", "flows", "glossary"] as const).map((category) => {
          const items = grouped[category];
          if (items.length === 0) return null;
          return (
            <section key={category}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t(`categories.${category}`)}
              </h3>
              <ul className="space-y-1">
                {items.map((a) => (
                  <li key={a.slug}>
                    <button
                      type="button"
                      onClick={() => selectSlug(a.slug)}
                      className="w-full rounded px-2 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                    >
                      {a.title}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
        {results.length === 0 && <p className="text-sm text-muted-foreground">{t("noResults")}</p>}
      </div>
    </div>
  );
}

export default HelpBrowser;
